import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './CreatePlaylistModal.css';

interface CreatePlaylistModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPlaylistCreated: (playlist: any) => void;
    songId?: number;
}

const CreatePlaylistModal: React.FC<CreatePlaylistModalProps> = ({
    isOpen,
    onClose,
    onPlaylistCreated,
    songId
}) => {
    const [playlistName, setPlaylistName] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!playlistName.trim()) {
            setError('Playlist name is required');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const requestBody: any = {
                name: playlistName.trim(),
                description: '',
                is_public: isPublic,
                theme: 'SING'
            };

            // If we have a songId, include it in the request
            if (songId) {
                requestBody.song_ids = [songId];
            }

            const response = await fetch('/api/playlists', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (response.ok) {
                onPlaylistCreated(data);
                handleClose();
            } else {
                setError(data.error || 'Failed to create playlist');
            }
        } catch (error) {
            console.error('Error creating playlist:', error);
            setError('Failed to create playlist');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setPlaylistName('');
        setIsPublic(false);
        setError('');
        setIsSubmitting(false);
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="modal-overlay"
            onClick={handleClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001
            }}
        >
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'white',
                    padding: '2rem',
                    borderRadius: '8px',
                    maxWidth: '400px',
                    width: '90%',
                    maxHeight: '80vh',
                    overflow: 'auto'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3>Create New Playlist</h3>
                    <button
                        onClick={handleClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            padding: '0.5rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor="playlist-name" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Playlist Name *
                        </label>
                        <input
                            id="playlist-name"
                            type="text"
                            value={playlistName}
                            onChange={(e) => setPlaylistName(e.target.value)}
                            placeholder="Enter playlist name"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '1rem'
                            }}
                            disabled={isSubmitting}
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.target.checked)}
                                disabled={isSubmitting}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <span>Make this playlist public</span>
                        </label>
                    </div>

                    {error && (
                        <div style={{
                            color: '#f44336',
                            marginBottom: '1rem',
                            padding: '0.5rem',
                            backgroundColor: '#ffebee',
                            borderRadius: '4px'
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            style={{
                                padding: '0.75rem 1.5rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !playlistName.trim()}
                            style={{
                                padding: '0.75rem 1.5rem',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: isSubmitting || !playlistName.trim() ? '#ccc' : '#007bff',
                                color: 'white',
                                cursor: isSubmitting || !playlistName.trim() ? 'not-allowed' : 'pointer',
                                fontSize: '1rem'
                            }}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Playlist'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default CreatePlaylistModal; 